import { useEffect, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useUser from 'store/user/useUser';
import { IPatientInfoSmall } from 'apis/admin/type';
import { getAssignment } from 'apis/survey/index';
import AssignmentHeader from './AssignmentHeader';
import useNotification from 'hooks/useNotification';
import useTable from 'hooks/useTable';
import { IUpedateSkillVideo } from 'apis/survey/type';
import { getSearchQuery } from 'utils/searchQuery';
import { Box } from '@mui/material';
import AssignmentBody from './AssignmentBody';
import useClassSection from 'store/section/useClassSection';

interface Props {
  patientInfo: IPatientInfoSmall;
}

const AssignmentCheck = ({ patientInfo }: Props) => {
  const { student_uuid: user_id } = useUser();
  const { search } = useLocation();
  const { onFail, onRequired } = useNotification();
  const { class_section_id } = useClassSection();
  const patient_id = patientInfo?.patient_id || '1';

  const {
    page = 1,
    keyword,
    searchType,
    sort_method,
    size,
  } = getSearchQuery(search);

  const {
    list,
    setList,
    totalCount,
    setTotalCount,
    isLoading,
    setIsLoading,
    onPageChange,
    selected,
    setSelected,
  } = useTable<IUpedateSkillVideo>();

  const [onlyUploadedFiles, setOnlyUploadedFiles] = useState(false);

  const onGetList = useCallback(() => {
    setIsLoading(true);

    getAssignment({
      page: String(page),
      patient_id: String(patient_id),
      user_id,
      keyword,
      searchType,
      sort_method,
      size,
      class_section_id,
    })
      .then(({ data }) => {
        const filteredList = onlyUploadedFiles
          ? data.student_assignment_info.filter(
              (student: any) =>
                student.student_assignment?.assignment1 ||
                student.student_assignment?.assignment2 ||
                student.student_assignment?.assignment3
            )
          : data.student_assignment_info;

        setList(filteredList);
        setTotalCount(data.count);
      })
      .catch(e => {
        const message = `가상환자 목록을 불러오는데 실패했습니다.`;
        onFail(message, e);
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line
  }, [
    page,
    keyword,
    searchType,
    sort_method,
    onlyUploadedFiles,
    class_section_id,
    size,
    patient_id,
  ]);

  useEffect(onGetList, [onGetList]);

  return (
    <Box flex={2} display="flex" flexDirection="column" overflow="auto">
      <AssignmentHeader
        totalCount={totalCount}
        onRequired={onRequired}
        currentSearchType={searchType as any}
        currentSortType={sort_method as any}
        currentSize={size as any}
        setList={setList}
        setTotalCount={setTotalCount}
        setIsLoading={setIsLoading}
        setOnlyUploadedFiles={setOnlyUploadedFiles}
        patientInfo={patientInfo}
        onlyUploadedFiles={onlyUploadedFiles}
      />
      <AssignmentBody
        totalCount={totalCount}
        list={list}
        isLoading={isLoading}
        page={Number(page) - 1}
        selected={selected}
        onSelected={setSelected}
        onPageChange={onPageChange}
      />
    </Box>
  );
};

export default AssignmentCheck;
